import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useI18n } from '@/hooks/useI18n';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { formatAmount } from '@/utils/formatters';

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface MonthColumn {
    month: number;
    month_name: string;
}

interface PLCategoryRow {
    category_id?: string;
    category_name: string;
    internal_id?: string;
    months: Record<number, number>;
}

interface PLData {
    year: number;
    months: MonthColumn[];
    revenue_categories: PLCategoryRow[];
    cogs_categories: PLCategoryRow[];
    overhead_categories: PLCategoryRow[];
    tax_categories: PLCategoryRow[];
    interest_categories: PLCategoryRow[];
    capex_categories: PLCategoryRow[];
    base_currency: string;
    cost_center_id?: string;
}

export default function ProfitLossPage() {
    const { t } = useI18n();
    const [plData, setPlData] = useState<PLData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);

    // Current date for default values
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
    const [selectedCostCenter, setSelectedCostCenter] = useState<string>('all');

    // Generate year options (current year and previous 5 years)
    const yearOptions: { value: number; label: string }[] = [];
    for (let i = 0; i < 6; i++) {
        const year = currentDate.getFullYear() - i;
        yearOptions.push({ value: year, label: year.toString() });
    }

    // Calculate totals for each month
    const calculateMonthlyTotals = (categories: PLCategoryRow[], months: MonthColumn[]) => {
        const totals: Record<number, number> = {};
        months.forEach(m => {
            totals[m.month] = categories.reduce((sum, cat) => sum + (cat.months[m.month] || 0), 0);
        });
        return totals;
    };

    // Calculate row totals
    const calculateRowTotal = (row: PLCategoryRow, months: MonthColumn[]) => {
        return months.reduce((sum, m) => sum + (row.months[m.month] || 0), 0);
    };

    const fetchProfitLoss = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                year: selectedYear.toString()
            });

            if (selectedCostCenter !== 'all') {
                params.append('cost_center_id', selectedCostCenter);
            }

            const response = await apiFetch(`/api/profit-loss/annual?${params}`);

            if (!response.ok) {
                throw new Error(`Failed to fetch P&L: ${response.status}`);
            }

            const data = await response.json();
            setPlData(data);
        } catch (err) {
            console.error('Error fetching P&L:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data.filter((cc: CostCenter) => cc.is_active));
            }
        } catch (error) {
            console.error('Error fetching cost centers:', error);
        }
    };

    // Load data on component mount and when filters change
    useEffect(() => {
        fetchCostCenters();
    }, []);

    useEffect(() => {
        fetchProfitLoss();
    }, [selectedYear, selectedCostCenter]);

    const handleGenerateReport = () => {
        fetchProfitLoss();
    };

    // Calculate all the P&L metrics
    const metrics = plData ? {
        totalIncome: calculateMonthlyTotals(plData.revenue_categories, plData.months),
        totalCOGS: calculateMonthlyTotals(plData.cogs_categories, plData.months),
        grossProfit: {} as Record<number, number>,
        totalOverhead: calculateMonthlyTotals(plData.overhead_categories, plData.months),
        totalTaxes: calculateMonthlyTotals(plData.tax_categories, plData.months),
        totalInterest: calculateMonthlyTotals(plData.interest_categories, plData.months),
        earningsBeforeInterest: {} as Record<number, number>,
        netEarnings: {} as Record<number, number>
    } : null;

    if (metrics && plData) {
        plData.months.forEach(m => {
            metrics.grossProfit[m.month] = metrics.totalIncome[m.month] - metrics.totalCOGS[m.month];
            metrics.earningsBeforeInterest[m.month] = metrics.grossProfit[m.month] - metrics.totalOverhead[m.month];
            metrics.netEarnings[m.month] = metrics.earningsBeforeInterest[m.month] - metrics.totalInterest[m.month] - metrics.totalTaxes[m.month];
        });
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('profitLoss.title', 'finance')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('profitLoss.subtitle', 'finance')}
                        </p>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-4 p-6 rounded-lg border bg-card">
                    <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">{t('costReports.year', 'finance')}:</Label>
                        <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                            <SelectTrigger className="w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map(option => (
                                    <SelectItem key={option.value} value={option.value.toString()}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">{t('costReports.costCenter', 'finance')}:</Label>
                        <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                            <SelectTrigger className="w-48">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('profitLoss.allCostCenters', 'finance')}</SelectItem>
                                {costCenters.map(cc => (
                                    <SelectItem key={cc.id} value={cc.id}>
                                        {cc.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button onClick={handleGenerateReport} disabled={loading}>
                        {loading ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                {t('profitLoss.loading', 'finance')}
                            </>
                        ) : (
                            t('profitLoss.refresh', 'finance')
                        )}
                    </Button>
                </div>

                {/* Error Display */}
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{t('profitLoss.error', 'finance')}: {error}</AlertDescription>
                    </Alert>
                )}

                {/* P&L Table */}
                {plData && metrics && (
                    <div className="rounded-lg border bg-card overflow-hidden max-w-[calc(100vw-16rem-3rem)] group-has-[[data-collapsible=icon]]/sidebar-wrapper:max-w-[calc(100vw-3rem-3rem)]">
                        <div className="relative">
                            <div className="overflow-x-auto">
                                <Table className="w-max min-w-full">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="sticky left-0 bg-muted z-20 whitespace-nowrap px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                            {t('costReports.category', 'finance')}
                                        </TableHead>
                                        {plData.months.map(month => (
                                            <TableHead key={month.month} className="text-center min-w-[80px] max-w-[100px] px-1 py-3 whitespace-nowrap">
                                                {month.month_name}
                                            </TableHead>
                                        ))}
                                        <TableHead className="text-center min-w-[120px] bg-muted px-2 py-3">
                                            {t('general.total', 'finance')}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/* Income Section */}
                                    <TableRow className="bg-muted/50 hover:bg-muted/70">
                                        <TableCell className="sticky left-0 bg-muted/50 z-20 font-bold border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                            {t('profitLoss.income', 'finance')}
                                        </TableCell>
                                        <TableCell className="bg-muted/50" colSpan={plData.months.length + 1}></TableCell>
                                    </TableRow>

                                    {plData.revenue_categories.map((cat, idx) => (
                                        <TableRow key={cat.category_id || `revenue-${idx}`}>
                                            <TableCell className="sticky left-0 bg-background z-20 font-medium px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                {cat.category_name}
                                            </TableCell>
                                            {plData.months.map(month => (
                                                <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                    {cat.months[month.month] != 0
                                                        ? formatAmount(cat.months[month.month], { currencyCode: plData.base_currency })
                                                        : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right bg-muted font-medium px-2 py-3 whitespace-nowrap tabular-nums">
                                                {formatAmount(calculateRowTotal(cat, plData.months), { currencyCode: plData.base_currency })}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Total Income */}
                                    <TableRow className="font-semibold">
                                        <TableCell className="sticky left-0 bg-background z-20 px-4 py-3 border-r border-border">
                                            {t('profitLoss.totalIncome', 'finance')}
                                        </TableCell>
                                        {plData.months.map(month => (
                                            <TableCell key={month.month} className="text-right px-2 py-3">
                                                {metrics.totalIncome[month.month] != 0 ? formatAmount(metrics.totalIncome[month.month], { currencyCode: plData.base_currency }) : '-'}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-right bg-muted font-bold px-2 py-3 whitespace-nowrap tabular-nums">
                                            {formatAmount(Object.values(metrics.totalIncome).reduce((a, b) => a + b, 0), { currencyCode: plData.base_currency })}
                                        </TableCell>
                                    </TableRow>

                                    {/* Operating Expenses Section */}
                                    <TableRow className="bg-muted/50 hover:bg-muted/70">
                                        <TableCell className="sticky left-0 bg-muted/50 z-20 font-bold border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                            {t('profitLoss.operatingExpenses', 'finance')}
                                        </TableCell>
                                        <TableCell className="bg-muted/50" colSpan={plData.months.length + 1}></TableCell>
                                    </TableRow>

                                    {/* COGS */}
                                    {plData.cogs_categories.map((cat, idx) => (
                                        <TableRow key={cat.category_id || `cogs-${idx}`}>
                                            <TableCell className="sticky left-0 bg-background z-20 font-medium px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                {cat.category_name}
                                            </TableCell>
                                            {plData.months.map(month => (
                                                <TableCell key={month.month} className="text-right px-2 py-3">
                                                    {cat.months[month.month] != 0
                                                        ? formatAmount(cat.months[month.month], { currencyCode: plData.base_currency })
                                                        : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right bg-muted font-medium px-2 py-3 whitespace-nowrap tabular-nums">
                                                {formatAmount(calculateRowTotal(cat, plData.months), { currencyCode: plData.base_currency })}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Total Operating Expenses */}
                                    <TableRow className="font-semibold">
                                        <TableCell className="sticky left-0 bg-background z-20 px-4 py-3 border-r border-border">
                                            {t('profitLoss.totalOperatingExpenses', 'finance')}
                                        </TableCell>
                                        {plData.months.map(month => (
                                            <TableCell key={month.month} className="text-right px-2 py-3">
                                                {metrics.totalCOGS[month.month] != 0 ? formatAmount(metrics.totalCOGS[month.month], { currencyCode: plData.base_currency }) : '-'}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-right bg-muted font-bold px-2 py-3 whitespace-nowrap tabular-nums">
                                            {formatAmount(Object.values(metrics.totalCOGS).reduce((a, b) => a + b, 0), { currencyCode: plData.base_currency })}
                                        </TableCell>
                                    </TableRow>

                                    {/* Gross Profit */}
                                    <TableRow className="bg-muted font-bold">
                                        <TableCell className="sticky left-0 bg-muted z-20 border-r border-border">
                                            {t('profitLoss.grossProfit', 'finance')}
                                        </TableCell>
                                        {plData.months.map(month => (
                                            <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                {metrics.grossProfit[month.month]  ? formatAmount(metrics.grossProfit[month.month], { currencyCode: plData.base_currency }) : '-'}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-center bg-muted font-bold px-2 py-3 whitespace-nowrap tabular-nums">
                                            {formatAmount(Object.values(metrics.grossProfit).reduce((a, b) => a + b, 0), { currencyCode: plData.base_currency })}
                                        </TableCell>
                                    </TableRow>

                                    {/* Overhead Expenses Section */}
                                    <TableRow className="bg-muted/50 hover:bg-muted/70">
                                        <TableCell className="sticky left-0 bg-muted/50 z-20 font-bold border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                            {t('profitLoss.overheadExpenses', 'finance')}
                                        </TableCell>
                                        <TableCell className="bg-muted/50" colSpan={plData.months.length + 1}></TableCell>
                                    </TableRow>

                                    {plData.overhead_categories.map((cat, idx) => (
                                        <TableRow key={cat.category_id || `overhead-${idx}`}>
                                            <TableCell className="sticky left-0 bg-background z-20 font-medium px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                {cat.category_name}
                                            </TableCell>
                                            {plData.months.map(month => (
                                                <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                    {cat.months[month.month] != 0
                                                        ? formatAmount(cat.months[month.month], { currencyCode: plData.base_currency })
                                                        : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right bg-muted font-medium px-2 py-3 whitespace-nowrap tabular-nums">
                                                {formatAmount(calculateRowTotal(cat, plData.months), { currencyCode: plData.base_currency })}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Total Expenses */}
                                    <TableRow className="font-semibold">
                                        <TableCell className="sticky left-0 bg-background z-20 px-4 py-3 border-r border-border">
                                            {t('profitLoss.totalExpenses', 'finance')}
                                        </TableCell>
                                        {plData.months.map(month => (
                                            <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                {metrics.totalOverhead[month.month] != 0 ? formatAmount(metrics.totalOverhead[month.month], { currencyCode: plData.base_currency }) : '-'}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-center bg-muted font-bold px-2 py-3 whitespace-nowrap tabular-nums">
                                            {formatAmount(Object.values(metrics.totalOverhead).reduce((a, b) => a + b, 0), { currencyCode: plData.base_currency })}
                                        </TableCell>
                                    </TableRow>

                                    {/* Earnings Before Interest */}
                                    <TableRow className="bg-muted font-bold text-sm">
                                        <TableCell className="sticky left-0 bg-muted z-20 border-r border-border">
                                            {t('profitLoss.earningsBeforeInterest', 'finance')}
                                        </TableCell>
                                        {plData.months.map(month => (
                                            <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                {metrics.earningsBeforeInterest[month.month] != 0 ? formatAmount(metrics.earningsBeforeInterest[month.month], { currencyCode: plData.base_currency }) : '-'}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-center bg-muted font-bold px-2 py-3 whitespace-nowrap tabular-nums">
                                            {formatAmount(Object.values(metrics.earningsBeforeInterest).reduce((a, b) => a + b, 0), { currencyCode: plData.base_currency })}
                                        </TableCell>
                                    </TableRow>

                                    {/* Interest and Taxes Section */}
                                    <TableRow className="bg-muted/50 hover:bg-muted/70">
                                        <TableCell className="sticky left-0 bg-muted/50 z-20 font-bold border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                            {t('profitLoss.interestAndTaxes', 'finance')}
                                        </TableCell>
                                        <TableCell className="bg-muted/50" colSpan={plData.months.length + 1}></TableCell>
                                    </TableRow>

                                    {/* Interest */}
                                    {plData.interest_categories.map((cat, idx) => (
                                        <TableRow key={cat.category_id || `interest-${idx}`}>
                                            <TableCell className="sticky left-0 bg-background z-20 font-medium px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                {cat.category_name}
                                            </TableCell>
                                            {plData.months.map(month => (
                                                <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                    {cat.months[month.month] != 0
                                                        ? formatAmount(cat.months[month.month], { currencyCode: plData.base_currency })
                                                        : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right bg-muted font-medium px-2 py-3 whitespace-nowrap tabular-nums">
                                                {formatAmount(calculateRowTotal(cat, plData.months), { currencyCode: plData.base_currency })}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Taxes */}
                                    {plData.tax_categories.map((cat, idx) => (
                                        <TableRow key={cat.category_id || `tax-${idx}`}>
                                            <TableCell className="sticky left-0 bg-background z-20 font-medium px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                {cat.category_name}
                                            </TableCell>
                                            {plData.months.map(month => (
                                                <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                    {cat.months[month.month] != 0
                                                        ? formatAmount(cat.months[month.month], { currencyCode: plData.base_currency })
                                                        : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right bg-muted font-medium px-2 py-3 whitespace-nowrap tabular-nums">
                                                {formatAmount(calculateRowTotal(cat, plData.months), { currencyCode: plData.base_currency })}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Net Earnings */}
                                    <TableRow className="bg-muted font-bold text-sm">
                                        <TableCell className="sticky left-0 bg-muted z-20 border-r border-border">
                                            {t('profitLoss.netEarnings', 'finance')}
                                        </TableCell>
                                        {plData.months.map(month => (
                                            <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                {metrics.netEarnings[month.month] != 0 ? formatAmount(metrics.netEarnings[month.month], { currencyCode: plData.base_currency }) : '-'}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-center bg-muted font-bold px-2 py-3 whitespace-nowrap tabular-nums">
                                            {formatAmount(Object.values(metrics.netEarnings).reduce((a, b) => a + b, 0), { currencyCode: plData.base_currency })}
                                        </TableCell>
                                    </TableRow>

                                    {/* CAPEX Section (not included in calculations) */}
                                    <TableRow className="bg-muted/50 hover:bg-muted/70">
                                        <TableCell className="sticky left-0 bg-muted/50 z-20 font-bold border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                            {t('profitLoss.capex', 'finance')}
                                        </TableCell>
                                        <TableCell className="bg-muted/50" colSpan={plData.months.length + 1}></TableCell>
                                    </TableRow>

                                    {plData.capex_categories.map((cat, idx) => (
                                        <TableRow key={cat.category_id || `capex-${idx}`}>
                                            <TableCell className="sticky left-0 bg-background z-20 font-medium px-4 py-3 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                {cat.category_name}
                                            </TableCell>
                                            {plData.months.map(month => (
                                                <TableCell key={month.month} className="text-right px-2 py-3 whitespace-nowrap tabular-nums">
                                                    {cat.months[month.month] != 0
                                                        ? formatAmount(cat.months[month.month], { currencyCode: plData.base_currency })
                                                        : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right bg-muted font-medium px-2 py-3 whitespace-nowrap tabular-nums">
                                                {formatAmount(calculateRowTotal(cat, plData.months), { currencyCode: plData.base_currency })}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && !plData && (
                    <div className="text-center py-8">
                        <div className="flex items-center justify-center space-x-2">
                            <Skeleton className="h-4 w-4 rounded-full" />
                            <Skeleton className="h-4 w-[150px]" />
                        </div>
                        <p className="mt-2 text-muted-foreground">{t('profitLoss.generatingReport', 'finance')}</p>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/profit-loss')({
  component: ProfitLossPage,
})
